import { forwardRef } from 'react';
import { useRef } from 'react';
import { useController } from 'react-hook-form';

import css from './checkbox.css';

import type { ChangeEventHandler, FocusEventHandler, KeyboardEvent, RefObject } from 'react';

interface CheckboxProps {
  label?: string;
  id: string;
  name?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  value?: string;
  defaultChecked?: boolean;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ value, label, onChange, onBlur, name, id, defaultChecked }, ref) => {
    const _ref = useRef<HTMLInputElement>(null);
    const innerRef = (ref ?? _ref) as RefObject<HTMLInputElement>;

    const handleKeypress = (e: KeyboardEvent<HTMLLabelElement>) => {
      if (e.key === 'Enter' || e.keyCode === 13) {
        innerRef.current?.click();
      }
    };

    return (
      <div className={css.wrapper}>
        <label className={css.label} htmlFor={id} tabIndex={0} onKeyPress={handleKeypress}>
          <input
            className={css.input}
            ref={innerRef}
            id={id}
            name={name}
            type="checkbox"
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            defaultChecked={defaultChecked}
          />
          {label && <span className={css.span} dangerouslySetInnerHTML={{ __html: label }} />}
        </label>
      </div>
    );
  }
);

type ControlledCheckboxProps = Omit<CheckboxProps, 'name' | 'id' | 'onChange'> & {
  name: string;
  id?: string;
};

export const ControlledCheckbox = ({ id, name, ...props }: ControlledCheckboxProps) => {
  const controller = useController({ name });
  return <Checkbox id={id ?? name} {...props} {...controller.field} />;
};
